// src/utils/invoicing/finvoiceRenderer.js
//
// Legacy Finvoice XML template (no version attribute).
// Accepts the same InvoiceData shape as pdfRenderer — knows nothing about grouping.
// Triggers a .xml file download the user can import into Finnish accounting software.
//
// Spec reference: https://www.finanssiala.fi/finvoice/

function xmlEscape(str) {
  if (!str && str !== 0) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;')
    // Encode non-ASCII as numeric XML entities so the file stays pure ASCII.
    // Required because Netvisor expects ISO-8859-15 and browsers can't natively
    // produce that encoding — numeric refs are encoding-agnostic.
    .replace(/[^ -]/g, c => `&#${c.codePointAt(0)};`);
}

function fmtFinvoiceDate(ms) {
  if (!ms) return '';
  // Finvoice uses CCYYMMDD
  return new Date(ms).toISOString().slice(0, 10).replace(/-/g, '');
}

// Finnish accounting software expects comma as decimal separator
function fmtAmount(n) {
  return Number(n || 0).toFixed(2).replace('.', ',');
}

function cleanIban(iban) {
  return (iban || '').replace(/\s/g, '');
}

// Derive VAT tax code from Y-number: "3610843-5" → "FI36108435"
function taxCode(yNumber) {
  return 'FI' + (yNumber || '').replace('-', '');
}

// Finnish bank BIC lookup by IBAN prefix (best-effort, not exhaustive)
const IBAN_BIC = {
  'FI40': 'NDEAFIHH', // Nordea
  'FI50': 'OKOYFIHH', // OP
  'FI58': 'OKOYFIHH',
  'FI33': 'DABAFIHH', // Danske
  'FI34': 'DABAFIHH',
  'FI36': 'TAPIFI22', // Tapiola / Handelsbanken
  'FI22': 'NDEAFIHH',
  'FI79': 'HELSFI22', // Aktia
  'FI31': 'HANDFIHH', // Handelsbanken
  'FI45': 'PSPBFIHH', // POP Pankki
  'FI47': 'POPFFI22',
  'FI53': 'AABAFI22', // Ålandsbanken
  'FI80': 'DABAFIHH',
};

function getBic(iban) {
  const key = (iban || '').replace(/\s/g, '').toUpperCase().slice(0, 4);
  return IBAN_BIC[key] || 'NDEAFIHH';
}

// ─── Party blocks ─────────────────────────────────────────────────────────────

function sellerPartyBlock(seller) {
  const iban = cleanIban(seller.iban);
  const bic  = getBic(iban);
  return `
  <SellerPartyDetails>
    <SellerPartyIdentifier>${xmlEscape(seller.yNumber)}</SellerPartyIdentifier>
    <SellerOrganisationName>${xmlEscape(seller.companyName)}</SellerOrganisationName>
    <SellerOrganisationTaxCode>${taxCode(seller.yNumber)}</SellerOrganisationTaxCode>
  </SellerPartyDetails>
  <SellerAccountDetails>
    <SellerAccountID IdentificationSchemeName="IBAN">${iban}</SellerAccountID>
    <SellerBic IdentificationSchemeName="BIC">${bic}</SellerBic>
  </SellerAccountDetails>`;
}

function buyerPartyBlock(buyer) {
  return `
  <BuyerPartyDetails>
    <BuyerPartyIdentifier>${xmlEscape(buyer.yNumber)}</BuyerPartyIdentifier>
    <BuyerOrganisationName>${xmlEscape(buyer.companyName)}</BuyerOrganisationName>
    <BuyerOrganisationTaxCode>${taxCode(buyer.yNumber)}</BuyerOrganisationTaxCode>
  </BuyerPartyDetails>`;
}

// ─── Invoice row blocks ───────────────────────────────────────────────────────

function invoiceRowBlock(row, alvRate) {
  // Unit price stored as gross; derive net and VAT for the row
  const gross   = Number(row.unitPrice || 0);
  const vatRate = Number(alvRate || 0);
  const net     = gross / (1 + vatRate / 100);
  const vat     = gross - net;

  const freeText = [
    row.origin && row.destination
      ? `Reitti: ${row.origin} - ${row.destination}`
      : null,
    row.id          ? `Tunnus: ${row.id}`             : null,
    row.date        ? `Toimitusaika: ${new Date(row.date).toLocaleDateString('fi-FI')}` : null,
  ].filter(Boolean).join(', ');

  return `
  <InvoiceRow>
    <ArticleIdentifier>${xmlEscape(row.articleId || 'PH-1')}</ArticleIdentifier>
    <ArticleName>${xmlEscape(row.articleName || '')}</ArticleName>
    <DeliveredQuantity QuantityUnitCode="kpl">1,00</DeliveredQuantity>
    <UnitPriceAmount AmountCurrencyIdentifier="EUR">${fmtAmount(net)}</UnitPriceAmount>
    <RowVatRatePercent>${fmtAmount(vatRate)}</RowVatRatePercent>
    <RowVatAmount AmountCurrencyIdentifier="EUR">${fmtAmount(vat)}</RowVatAmount>
    <RowAmount AmountCurrencyIdentifier="EUR">${fmtAmount(gross)}</RowAmount>
    ${freeText ? `<RowFreeText>${xmlEscape(freeText)}</RowFreeText>` : ''}
  </InvoiceRow>`;
}

function billingFeeRowBlock(fee, alvRate) {
  const gross   = Number(fee || 0);
  const vatRate = Number(alvRate || 0);
  const net     = gross / (1 + vatRate / 100);
  const vat     = gross - net;

  return `
  <InvoiceRow>
    <ArticleIdentifier>L-1</ArticleIdentifier>
    <ArticleName>Palvelumaksu</ArticleName>
    <DeliveredQuantity QuantityUnitCode="kpl">1,00</DeliveredQuantity>
    <UnitPriceAmount AmountCurrencyIdentifier="EUR">${fmtAmount(net)}</UnitPriceAmount>
    <RowVatRatePercent>${fmtAmount(vatRate)}</RowVatRatePercent>
    <RowVatAmount AmountCurrencyIdentifier="EUR">${fmtAmount(vat)}</RowVatAmount>
    <RowAmount AmountCurrencyIdentifier="EUR">${fmtAmount(gross)}</RowAmount>
    <RowFreeText>Palvelumaksu</RowFreeText>
  </InvoiceRow>`;
}

// ─── EPI (payment) block ──────────────────────────────────────────────────────

function epiBlock(seller, invoiceData) {
  const iban = cleanIban(seller.iban);
  const bic  = getBic(iban);
  return `
  <EpiDetails>
    <EpiIdentificationDetails>
      <EpiDate Format="CCYYMMDD">${fmtFinvoiceDate(invoiceData.dueAt)}</EpiDate>
    </EpiIdentificationDetails>
    <EpiPartyDetails>
      <EpiBfiPartyDetails>
        <EpiBfiIdentifier IdentificationSchemeName="BIC">${bic}</EpiBfiIdentifier>
      </EpiBfiPartyDetails>
      <EpiBeneficiaryPartyDetails>
        <EpiNameAddressDetails>${xmlEscape(seller.companyName || '')}</EpiNameAddressDetails>
        <EpiAccountID IdentificationSchemeName="IBAN">${iban}</EpiAccountID>
      </EpiBeneficiaryPartyDetails>
    </EpiPartyDetails>
    <EpiPaymentInstructionDetails>
      <EpiPaymentMethod>42</EpiPaymentMethod>
      <EpiAmount AmountCurrencyIdentifier="EUR">${fmtAmount(invoiceData.gross)}</EpiAmount>
    </EpiPaymentInstructionDetails>
  </EpiDetails>`;
}

// ─── Download helper ──────────────────────────────────────────────────────────

function downloadXml(xml, filename) {
  const blob = new Blob([xml], { type: 'application/xml;charset=iso-8859-15' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ─── Public renderer ──────────────────────────────────────────────────────────

/**
 * Renders an outgoing or purchase invoice as a legacy Finvoice XML file.
 *
 * @param {object} seller      - org payment info { companyName, yNumber, iban, address }
 * @param {object} invoiceData - from buildOutgoingGroups or buildPurchaseGroups
 * @param {'outgoing'|'purchase'} type
 */
export function renderFinvoice(seller, invoiceData, type = 'outgoing') {
  const alvRate = invoiceData.alvRate;
  // InvoiceNumber must be purely numeric — use the issuedAt timestamp.
  // The human-readable ID is preserved in SellerReferenceIdentifier.
  const numericInvoiceNumber = String(invoiceData.issuedAt);

  // Derive billing fee from the difference between invoice gross total and sum of ride rows.
  // Guards against billingFee being 0/null even when the totals include it.
  const rowGrossSum     = invoiceData.rows.reduce((sum, r) => sum + Number(r.unitPrice || 0), 0);
  const impliedFeeGross = Number(invoiceData.gross) - rowGrossSum;
  const billingFeeGross = Number(invoiceData.billingFee) || (impliedFeeGross > 0.005 ? impliedFeeGross : 0);

  const allRows = [
    ...invoiceData.rows.map(r => invoiceRowBlock(r, alvRate)),
    ...(billingFeeGross > 0 ? [billingFeeRowBlock(billingFeeGross, alvRate)] : []),
  ].join('');

  const xml = `<?xml version="1.0" encoding="ISO-8859-15"?>
  <?xml-stylesheet type="text/xsl" href="Finvoice.xsl"?>
  <Finvoice>
  ${sellerPartyBlock(seller)}
  ${buyerPartyBlock(invoiceData.buyer)}
  <InvoiceDetails>
    <InvoiceTypeCode>INV01</InvoiceTypeCode>
    <InvoiceNumber>${numericInvoiceNumber}</InvoiceNumber>
    <InvoiceDate Format="CCYYMMDD">${fmtFinvoiceDate(invoiceData.issuedAt)}</InvoiceDate>
    <SellerReferenceIdentifier>${xmlEscape(invoiceData.invoiceNumber)}</SellerReferenceIdentifier>
    <InvoiceDueDate Format="CCYYMMDD">${fmtFinvoiceDate(invoiceData.dueAt)}</InvoiceDueDate>
    <InvoiceTotalVatExcludedAmount AmountCurrencyIdentifier="EUR">${fmtAmount(invoiceData.net)}</InvoiceTotalVatExcludedAmount>
    <InvoiceTotalVatAmount AmountCurrencyIdentifier="EUR">${fmtAmount(invoiceData.alvAmount)}</InvoiceTotalVatAmount>
    <InvoiceTotalVatIncludedAmount AmountCurrencyIdentifier="EUR">${fmtAmount(invoiceData.gross)}</InvoiceTotalVatIncludedAmount>
  </InvoiceDetails>
  ${allRows}
  ${epiBlock(seller, invoiceData)}
  </Finvoice>`;

  const prefix = type === 'purchase' ? 'ostolasku' : 'lasku';
  downloadXml(xml, `${prefix}-${invoiceData.invoiceNumber}.xml`);
}